import React from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { AdminColors } from '../constants/adminColors';
import FontFamily from '../../../constants/fonts';
import { useTheme } from '../../../theme/useTheme';

export interface SelectOption {
  label: string;
  value: string;
}

interface AdminSelectProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
}

/**
 * Cross-platform select for admin forms.
 * On web: native <select> styled to match AdminTextInput.
 * On native: cycles through options on press.
 */
const AdminSelect: React.FC<AdminSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  if (Platform.OS === 'web') {
    const Tag = 'select' as any;
    const Opt = 'option' as any;

    const webStyle: React.CSSProperties & Record<string, any> = isDark
      ? {
          ...webSelectStyle,
          backgroundColor: colors.input.background,
          borderColor: colors.input.border,
          color: colors.text.primary,
          colorScheme: 'dark',
        }
      : webSelectStyle;

    return (
      <Tag
        value={value ?? ''}
        onChange={(e: any) => onChange(e.target.value)}
        style={webStyle}
      >
        {placeholder !== undefined && (
          <Opt value="">{placeholder}</Opt>
        )}
        {options.map(o => (
          <Opt key={o.value} value={o.value}>{o.label}</Opt>
        ))}
      </Tag>
    );
  }

  // Native fallback: cycle through options on tap
  const currentIndex = options.findIndex(o => o.value === (value ?? ''));
  const currentLabel = currentIndex >= 0 ? options[currentIndex].label : (placeholder ?? 'Select...');

  const handlePress = () => {
    const nextIndex = (currentIndex + 1) % options.length;
    onChange(options[nextIndex].value);
  };

  return (
    <TouchableOpacity
      style={[
        styles.nativeSelect,
        isDark && {
          backgroundColor: colors.input.background,
          borderColor: colors.input.border,
        },
      ]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <Text style={[styles.nativeSelectText, isDark && { color: colors.text.primary }]}>
        {currentLabel}
      </Text>
      <Text style={[styles.nativeArrow, isDark && { color: colors.text.disabled }]}>▾</Text>
    </TouchableOpacity>
  );
};

// Inline style object for web <select> — matches AdminTextInput look
const webSelectStyle: React.CSSProperties & Record<string, any> = {
  height: 36,
  paddingLeft: 10,
  paddingRight: 10,
  paddingTop: 6,
  paddingBottom: 6,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: AdminColors.borderDefault,
  borderRadius: 5,
  fontSize: 13,
  color: AdminColors.textPrimary,
  backgroundColor: AdminColors.bgSurface,
  fontFamily: FontFamily.regular,
  width: '100%',
  cursor: 'pointer',
  appearance: 'auto',
};

const styles = StyleSheet.create({
  nativeSelect: {
    height: 36,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: AdminColors.bgSurface,
  },
  nativeSelectText: {
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    flex: 1,
  },
  nativeArrow: {
    fontSize: 12,
    color: AdminColors.textMuted,
    marginLeft: 4,
  },
});

export default AdminSelect;
